"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { verifySessionCookies } from "@repo/ui/actions/session";
import { setupFetchInterceptor } from "@repo/ui/lib/fetchInterceptor";
import { getTabSessionId, setTabSessionId } from "@repo/ui/lib/tabSessionId";
import { useLocalizedRouter } from "@repo/ui/navigation";

import { apiPath } from "../../constants/bookingBase";
import { FlightSpinLoader } from "../common/Loader/FlightSpinLoader";

const SESSION_STORAGE_KEY = "tab_session_id";

type HydratorMode = "home" | "guard";

type SessionIdHydratorProps = {
  redirectPath?: string;
  mode?: HydratorMode;
  homeUrl?: string;
};

function clearClientSessionCache() {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.name = "";
  } catch {
    // ignore
  }

  if (typeof globalThis.sessionStorage === "undefined") {
    return;
  }

  try {
    globalThis.sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // ignore
  }
}

async function initializeServerSession(): Promise<string | null> {
  const response = await fetch(apiPath("/api/init-session"), { cache: "no-store" });
  const data = await response.json().catch(() => ({}));

  if (data?.sessionId) {
    setTabSessionId(data.sessionId);
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("session-initialized", { detail: { sessionId: data.sessionId } }),
      );
    }
    return data.sessionId as string;
  }

  return null;
}

export default function SessionIdHydrator({
  redirectPath,
  mode = "home",
  homeUrl,
}: SessionIdHydratorProps) {
  const router = useLocalizedRouter();
  const initRef = useRef(false);
  const [isInitializing, setIsInitializing] = useState(false);

  const recoverSession = useCallback(
    async (shouldRedirect: boolean) => {
      if (mode === "guard") {
        return;
      }
      clearClientSessionCache();

      try {
        const newSessionId = await initializeServerSession();

        if (!newSessionId) {
          throw new Error("Session API did not return a sessionId");
        }

        if (shouldRedirect && redirectPath) {
          router.replace(redirectPath);
        }
      } catch (error) {
        console.error("[SessionIdHydrator] Failed to initialize session", error);
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("session-initialized", { detail: { error: true } }));
        }
      }
    },
    [mode, redirectPath, router],
  );

  const redirectToHome = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    const target = homeUrl || "/";
    window.location.assign(target);
  }, [homeUrl]);

  const handleInvalidSession = useCallback(async () => {
    if (mode === "guard") {
      redirectToHome();
      return;
    }

    await recoverSession(Boolean(redirectPath));
  }, [mode, redirectPath, recoverSession, redirectToHome]);

  const shouldSyncSessionId = useCallback(
    (existingSessionId: string | null, cookieSessionId: string | null) => {
      if (cookieSessionId && cookieSessionId !== existingSessionId) {
        setTabSessionId(cookieSessionId);
      }
    },
    [],
  );

  useEffect(() => {
    setupFetchInterceptor();
  }, []);

  useEffect(() => {
    if (initRef.current) {
      return;
    }
    initRef.current = true;

    let cancelled = false;

    const ensureSession = async () => {
      setIsInitializing(true);
      try {
        const existingSessionId = getTabSessionId();

        const hasWindowName = typeof window !== "undefined" && window.name !== "";
        const hasSessionStorage =
          typeof globalThis.sessionStorage !== "undefined" &&
          globalThis.sessionStorage.getItem(SESSION_STORAGE_KEY) !== null;

        const isDuplicateTab = hasWindowName && !hasSessionStorage;

        const validation = await verifySessionCookies(existingSessionId);

        if (cancelled) {
          return;
        }

        shouldSyncSessionId(existingSessionId, validation.cookieSessionId);

        const hasValidSession = Boolean(existingSessionId) && validation.valid && !isDuplicateTab;

        if (hasValidSession) {
          return;
        }

        await handleInvalidSession();
      } finally {
        if (!cancelled) {
          setIsInitializing(false);
        }
      }
    };

    ensureSession().catch((error) => {
      console.error("[SessionIdHydrator] Unexpected error", error);
      setIsInitializing(false);
    });

    return () => {
      cancelled = true;
    };
  }, [handleInvalidSession, shouldSyncSessionId]);

  if (isInitializing) {
    return (
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-white"
        style={{ pointerEvents: "all" }}
      >
        <FlightSpinLoader size="large" />
      </div>
    );
  }

  return null;
}
